// The knife in the player's hand in first person, put together like ET's
// CG_AddViewWeapon: the hands model at the eye, moved by CG_CalculateWeaponPosition
// (walk bob, idle drift, the dip after a landing, lower at a wide field of
// view), the arm with the team's sleeve on its tag_weapon and the blade on
// tag_barrel. It is drawn over the world (RF_DEPTHHACK: its own pass with the
// depth cleared, with a near plane close enough for the hand) and lit at the
// eye with at least RF_MINLIGHT's ambient light.

import * as THREE from 'three';
import { MeshPart } from '../render/entityParts';
import { placeOnTag, type Orientation } from '../render/models/skeleton';
import { angleVectors, type Vec3 } from '../shared/math';
import type { ViewWeaponAssets } from './playerAssets';
import type { PlayerRenderKit } from './playerModel';

/** cg.landChange / cg.landTime timing (cg_local.h). */
const LAND_DEFLECT_TIME = 150;
const LAND_RETURN_TIME = 300;
/** R_SetupEntityLighting: RF_MINLIGHT ambient. */
const MIN_LIGHT = 64;
/** weapon.cfg: IDLE1 is frame 0 of the hands. */
const IDLE_FRAME = 0;

export interface ViewWeaponFrame {
  /** cg.refdef vieworg and refdefViewAngles. */
  eye: Vec3;
  angles: Vec3;
  velocity: ArrayLike<number>;
  /** ps->bobCycle (PM_Footsteps). */
  bobCycle: number;
  team: 0 | 1;
  /** cg_fov (the horizontal field of view on a 4:3 screen). */
  fov: number;
  /** ms */
  time: number;
}

export class ViewWeapon {
  readonly scene = new THREE.Scene();
  /** The world camera's view with ET's near plane (r_znear 3; the hand is a few units from the eye). */
  readonly camera = new THREE.PerspectiveCamera(75, 1, 3, 1024);
  private readonly hands: MeshPart;
  /** The arm with each team's skin; the one of the player's team is shown. */
  private readonly arms: [MeshPart, MeshPart];
  private readonly parts: { part: MeshPart; tag: string }[];
  private landChange = 0;
  private landTime = -Infinity;

  constructor(
    private readonly kit: PlayerRenderKit,
    assets: ViewWeaponAssets,
  ) {
    this.camera.up.set(0, 0, 1);
    this.hands = new MeshPart(this.scene, kit, assets.hands, null, false);
    this.arms = [new MeshPart(this.scene, kit, assets.model, assets.skins[0], false), new MeshPart(this.scene, kit, assets.model, assets.skins[1] ?? assets.skins[0], false)];
    this.parts = assets.parts.map((p) => ({ part: new MeshPart(this.scene, kit, p.model, null, false), tag: p.tag }));
    for (const p of [this.hands, ...this.arms, ...this.parts.map((x) => x.part)]) p.setFrame(IDLE_FRAME, IDLE_FRAME, 0);
  }

  /**
   * The player landed (EV_FALL_*): the weapon dips and comes back.
   * @param delta PM_CrashLand's delta
   */
  land(delta: number, time: number): void {
    if (delta <= 7) return;
    this.landChange = delta > 58 ? -24 : delta > 38.75 ? -16 : -8;
    this.landTime = time;
  }

  /** CG_CalculateWeaponPosition: where the hands are and how they are turned. */
  private position(f: ViewWeaponFrame): Orientation {
    const origin: Vec3 = [f.eye[0], f.eye[1], f.eye[2]];
    const angles: Vec3 = [f.angles[0], f.angles[1], f.angles[2]];
    // gun angles from bobbing, inverted on odd legs
    const xyspeed = Math.hypot(f.velocity[0], f.velocity[1]);
    const bobfracsin = Math.abs(Math.sin(((f.bobCycle & 127) / 127) * Math.PI));
    const scale = (f.bobCycle & 128) >> 7 ? -xyspeed : xyspeed;
    angles[2] += scale * bobfracsin * 0.005;
    angles[1] += scale * bobfracsin * 0.01;
    angles[0] += xyspeed * bobfracsin * 0.005;
    // drop the weapon when landing
    const delta = f.time - this.landTime;
    if (delta < LAND_DEFLECT_TIME) origin[2] += (this.landChange * 0.25 * delta) / LAND_DEFLECT_TIME;
    else if (delta < LAND_DEFLECT_TIME + LAND_RETURN_TIME) origin[2] += (this.landChange * 0.25 * (LAND_DEFLECT_TIME + LAND_RETURN_TIME - delta)) / LAND_RETURN_TIME;
    // idle drift
    const drift = 80 * Math.sin(f.time * 0.001) * 0.01;
    angles[0] += drift;
    angles[1] += drift;
    angles[2] += drift;
    // CG_AddViewWeapon: drop the gun lower at a higher fov (cg_gun_x/y/z are 0), along the view's up axis
    const fovOffset = f.fov > 90 ? -0.2 * (f.fov - 90) : 0;
    const viewUp: Vec3 = [0, 0, 0];
    angleVectors(f.angles, null, null, viewUp);
    for (let k = 0; k < 3; k++) origin[k] += fovOffset * viewUp[k];
    const forward: Vec3 = [0, 0, 0];
    const right: Vec3 = [0, 0, 0];
    const up: Vec3 = [0, 0, 0];
    angleVectors(angles, forward, right, up);
    return { origin, axis: [forward, [-right[0], -right[1], -right[2]], up] };
  }

  /** Places, lights and draws the weapon over what the renderer drew with `world` (call after the world pass). */
  render(renderer: THREE.WebGLRenderer, world: THREE.PerspectiveCamera, f: ViewWeaponFrame): void {
    const hand = this.position(f);
    this.hands.place(hand);
    const light = this.kit.light(f.eye, f.eye, false);
    for (let k = 0; k < 3; k++) light.ambient[k] = Math.max(light.ambient[k], MIN_LIGHT);
    const arm = this.arms[f.team];
    this.arms[f.team ? 0 : 1].group.visible = false;
    const tag = this.hands.tag('tag_weapon');
    arm.group.visible = !!tag;
    if (tag) {
      arm.place(placeOnTag(hand, tag));
      arm.light(light);
    }
    for (const p of this.parts) {
      const t = this.hands.tag(p.tag);
      p.part.group.visible = !!t;
      if (!t) continue;
      p.part.place(placeOnTag(hand, t));
      p.part.light(light);
    }

    const cam = this.camera;
    if (cam.fov !== world.fov || cam.aspect !== world.aspect) {
      cam.fov = world.fov;
      cam.aspect = world.aspect;
      cam.updateProjectionMatrix();
    }
    cam.position.copy(world.position);
    cam.quaternion.copy(world.quaternion);
    cam.updateMatrixWorld();
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(this.scene, cam);
    renderer.autoClear = autoClear;
  }

  dispose(): void {
    this.hands.dispose();
    for (const a of this.arms) a.dispose();
    for (const p of this.parts) p.part.dispose();
  }
}
